import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Toaster } from "sonner";
import DashboardPage from "@/pages/Dashboard";
import Login from "@/pages/Login";
import SetPassword from "@/pages/SetPassword";
import OAuthCallback from "@/pages/auth/OAuthCallback";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import AcceptInvitation from "@/pages/auth/AcceptInvitation";
import { useEffect, type ReactNode } from "react";
import MainLayout from "@/components/layout/MainLayout";
import Calender from "@/pages/Calender";
import SearchResultsPage from "./components/layout/SearchResults";
import SettingsPage from "./pages/Settings";
import LeadsPage from "./pages/Stages/Leads";
import ProjectsPage from "./pages/Projects";
import UsersPage from "./pages/Users";
import AuditLogs from "./pages/AuditLogs";
import ClientsPage from "./pages/Clients";
import DialogHost from "@/components/ui/dialog-host";
import InvoicesPage from "./pages/Invoices";
import ClientInvoicesPage from "./pages/finance/ClientInvoices";
import InvoiceNewPage from "./pages/finance/InvoiceNew";
import InvoiceDetailPage from "./pages/finance/InvoiceDetail";
import ExpensesPage from "./pages/finance/Expenses";
import ExpenseFormPage from "./pages/finance/ExpenseForm";
import ExpenseDetailPage from "./pages/finance/ExpenseDetail";
import ExpenseCategoriesPage from "./pages/finance/ExpenseCategories";
import ExpenseSubscriptionsPage from "./pages/finance/ExpenseSubscriptions";
import ContactsPage from "./pages/crm/Contacts";
import TasksPage from "./pages/crm/Tasks";
import DeliveryProjectsPage from "./pages/crm/DeliveryProjects";
import ProductsPage from "./pages/sales/Products";
import QuotesPage from "./pages/sales/Quotes";
import QuoteDetailPage from "./pages/sales/QuoteDetail";
import ContractsPage from "./pages/sales/Contracts";
import ContractFormPage from "./pages/sales/ContractForm";
import ContractDetailPage from "./pages/sales/ContractDetail";
import PaymentsPage from "./pages/finance/Payments";
import DocumentsPage from "./pages/workspace/Documents";
import ReportsPage from "./pages/workspace/Reports";
import OrganizationPage from "./pages/organization/OrganizationPage";
import PlatformPage from "./pages/platform/PlatformPage";
import { isLoggedIn as checkIsLoggedIn, isPlatformAdmin, loadSession } from "@/utils/service";






/** Pages a system administrator may open: the platform console and their own account settings. */
const PLATFORM_PATHS = /^\/(platform(\/|$)|settings(\/|$))/;

const ProtectedRoute = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  if (!checkIsLoggedIn()) {
    return <Navigate to="/login" replace />;
  }
  // System administrators never see organization pages; organization members never see the platform console.
  if (isPlatformAdmin() && !PLATFORM_PATHS.test(pathname)) {
    return <Navigate to="/platform" replace />;
  }
  if (!isPlatformAdmin() && /^\/platform(\/|$)/.test(pathname)) {
    return <Navigate to="/" replace />;
  }
  return children;
};

const PublicRoute = ({ children }: { children: ReactNode }) => {
  if (checkIsLoggedIn()) {
    return <Navigate to={isPlatformAdmin() ? "/platform" : "/"} replace />;
  }
  return children;
};

// Wrapper component for pages that need MainLayout
const LayoutWrapper = ({ children }: { children: ReactNode }) => {
  return <MainLayout>{children}</MainLayout>;
};

function AppContent() {
  return (
    <Router>
      <DialogHost />
      <div className="min-h-screen bg-background">
        <Routes>
          {/* Public Routes - No Layout */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path="/set-password"
            element={
              <PublicRoute>
                <SetPassword />
              </PublicRoute>
            }
          />
          <Route
            path="/reset-password"
            element={
              <PublicRoute>
                <SetPassword />
              </PublicRoute>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <PublicRoute>
                <ForgotPassword />
              </PublicRoute>
            }
          />
          {/* Reachable signed in or out: OAuth returns here, and invitations can be accepted by existing users. */}
          <Route path="/auth/callback" element={<OAuthCallback />} />
          <Route path="/accept-invitation" element={<AcceptInvitation />} />

          {/* Protected Routes - All wrapped in MainLayout */}
          {/* Dashboard */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <DashboardPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/search"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <SearchResultsPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          {/* Workflow Routes */}

<Route
            path="/sales/leads"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <LeadsPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          {/* Projects Routes */}
          <Route
            path="/projects/all"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <ProjectsPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          {/* Pipeline Routes */}

<Route
            path="/users"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <UsersPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          {/* Clients Routes */}
          <Route
            path="/clients"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <ClientsPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients/new"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <ClientsPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          {/* Invoices Route */}
          <Route
            path="/invoices"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <InvoicesPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/invoices/client/:clientId"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <ClientInvoicesPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices/client/:clientId/new"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <InvoiceNewPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices/:id"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <InvoiceDetailPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/expenses"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <ExpensesPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/expenses/new"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <ExpenseFormPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/expenses/categories"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <ExpenseCategoriesPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/expenses/subscriptions"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <ExpenseSubscriptionsPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/expenses/:id/edit"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <ExpenseFormPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/expenses/:id"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <ExpenseDetailPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          {/* Team Routes */}

          {/* Analytics Routes */}

          {/* Other Routes */}
          <Route
            path="/contacts"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <ContactsPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <TasksPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/delivery"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <DeliveryProjectsPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/products"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <ProductsPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotes"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <QuotesPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotes/new"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <QuoteDetailPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotes/:id"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <QuoteDetailPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route path="/contracts/new" element={<ProtectedRoute><LayoutWrapper><ContractFormPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/contracts/:id/edit" element={<ProtectedRoute><LayoutWrapper><ContractFormPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/contracts/:id" element={<ProtectedRoute><LayoutWrapper><ContractDetailPage /></LayoutWrapper></ProtectedRoute>} />
          <Route
            path="/contracts"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <ContractsPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <PaymentsPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/documents"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <DocumentsPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <ReportsPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/organization"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <OrganizationPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/platform/:section?"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <PlatformPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <Calender />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/audit-logs"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <AuditLogs />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <SettingsPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="*"
            element={
              checkIsLoggedIn() ? (
                <Navigate to="/" replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        </Routes>
        <Toaster position="bottom-right" closeButton richColors={false} />
      </div>
    </Router>
  );
}

function App() {
  // Refresh the user's organizations and permissions on start (they may have changed since sign-in).
  useEffect(() => {
    if (checkIsLoggedIn()) loadSession().catch(() => {});
  }, []);
  return <AppContent />;
}

export default App;